import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { BLOG_POSTS } from "../lib/exhibitionData";

function PostBody({ post }) {
  // Three item shapes supported per body element:
  //   { heading, text }  → red uppercase section heading + prose paragraph
  //   { quote }          → italic pull quote in the exhibition serif
  //   { text } | string  → plain body paragraph
  if (post.body && post.body.length > 0 && typeof post.body[0] === "object") {
    return (
      <div className="space-y-8">
        {post.body.map((item, i) => {
          if (item.quote) {
            return (
              <blockquote
                key={i}
                className="border-l-2 border-[#C41E1E] pl-5 md:pl-6 font-serif-display italic text-xl md:text-2xl leading-[1.35] text-[#F5F0E8]"
              >
                {`\u201C${item.quote}\u201D`}
              </blockquote>
            );
          }
          return (
            <div key={i}>
              {item.heading && (
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C41E1E] font-semibold mb-3">
                  {item.heading}
                </p>
              )}
              <p className="text-base md:text-lg leading-relaxed text-[#F5F0E8]/85">
                {item.text}
              </p>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {post.body.map((p, i) => (
        <p
          key={i}
          className="text-base md:text-lg leading-relaxed text-[#F5F0E8]/85"
        >
          {p}
        </p>
      ))}
    </div>
  );
}

function PostModal({ post, onClose }) {
  useEffect(() => {
    if (!post) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [post, onClose]);

  if (!post) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 pl-nav-blur flex items-start md:items-center justify-center p-4 md:p-10 overflow-y-auto"
      onClick={onClose}
      data-testid="fieldnote-modal"
    >
      <article
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#0F0F0F] border border-[#F5F0E8]/10 max-w-3xl w-full my-8 md:my-0"
      >
        <button
          onClick={onClose}
          data-testid="fieldnote-modal-close"
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-[#F5F0E8] hover:text-[#C41E1E] transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="aspect-[16/9] w-full overflow-hidden bg-[#111]">
          <img
            src={post.cover}
            alt={post.alt}
            className="w-full h-full object-cover pl-no-bw"
          />
        </div>
        <div className="p-6 md:p-10 lg:p-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[#F5F0E8]/55 mb-5">
            <span className="text-[#C41E1E] font-semibold">
              {post.category}
            </span>
            <span className="w-4 h-[1px] bg-[#F5F0E8]/30" />
            <span>{post.date}</span>
          </div>
          {post.kicker && (
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#F5F0E8]/60 mb-3">
              {post.kicker}
            </p>
          )}
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#F5F0E8] mb-8">
            {post.title}
          </h2>
          <PostBody post={post} />
          {post.signature && (
            <p className="mt-10 text-[11px] uppercase tracking-[0.3em] text-[#F5F0E8]/65">
              {post.signature}
            </p>
          )}
        </div>
      </article>
    </div>
  );
}

export default function BlogUpdates() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="updates"
      data-testid="blog-updates-section"
      className="relative bg-[#0A0A0A] text-[#F5F0E8] border-b border-[#C41E1E]/30"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10 md:mb-14">
          <div className="lg:col-span-6">
            <span className="overline text-[#C41E1E]">Field Notes</span>
            <h2 className="mt-4 font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Updates from <br />
              <span className="italic">the gallery floor.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base md:text-lg text-[#F5F0E8]/75 leading-relaxed">
              Short letters, conversations, and reading lists from the
              curatorial team as the exhibition takes shape.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {BLOG_POSTS.map((post, i) => (
            <article
              key={post.id}
              className="flex flex-col group cursor-pointer"
              data-testid={`blog-post-${i}`}
              onClick={() => setActive(post)}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#111]">
                <img
                  src={post.cover}
                  alt={post.alt || post.title}
                  className="w-full h-full object-cover pl-no-bw transition-transform duration-1000 group-hover:scale-[1.03]"
                  loading="lazy"
                />
              </div>
              <div className="mt-6 border-t border-[#F5F0E8]/20 pt-5">
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-[#F5F0E8]/55">
                  <span className="text-[#C41E1E] font-semibold">
                    {post.category}
                  </span>
                  <span className="w-4 h-[1px] bg-[#F5F0E8]/30" />
                  <span>{post.date}</span>
                </div>
                <h3 className="mt-4 font-serif-display text-2xl md:text-3xl leading-tight group-hover:text-[#C41E1E] transition-colors duration-500">
                  {post.title}
                </h3>
                <p className="mt-3 text-[#F5F0E8]/70 text-sm md:text-base leading-relaxed">
                  {post.excerpt}
                </p>
                <button
                  type="button"
                  data-testid={`blog-read-more-${i}`}
                  className="pl-link mt-5 inline-block text-[11px] uppercase tracking-[0.3em] font-semibold text-[#F5F0E8]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActive(post);
                  }}
                >
                  {post.readMoreLabel || "Read the full piece"} →
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      <PostModal post={active} onClose={() => setActive(null)} />
    </section>
  );
}
